import { useState, useCallback } from 'react';
import { CreateQuoteRequest, Quote, ApiResponse } from '../types/index';
import { api } from '../services/api';

export const useQuotes = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createQuote = useCallback(async (quoteData: CreateQuoteRequest): Promise<ApiResponse<Quote>> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.createQuote(quoteData);
      
      // Check if response is valid and has the expected structure
      if (response && typeof response === 'object') {
        if (!response.success) {
          setError(response.message ?? 'Failed to create quote');
        }
        return response;
      } else {
        // Handle case where response is not a proper object
        const errorMessage = 'Invalid response from server';
        setError(errorMessage);
        return {
          success: false,
          message: errorMessage,
          data: null as any,
        };
      }
    } catch (err: any) {
      // Enhanced error handling
      let errorMessage = 'Failed to create quote';
      
      if (err instanceof SyntaxError) {
        errorMessage = 'Server returned invalid JSON response';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
        data: null as any,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const getQuotesByClient = useCallback(async (clientId: number): Promise<ApiResponse<Quote[]>> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getQuotesByClient(clientId);
      
      // Check if response is valid and has the expected structure
      if (response && typeof response === 'object') {
        if (!response.success) {
          setError(response.message ?? 'Failed to fetch quotes');
        }
        return response;
      } else {
        // Handle case where response is not a proper object
        const errorMessage = 'Invalid response from server';
        setError(errorMessage);
        return {
          success: false,
          message: errorMessage,
          data: [],
        };
      }
    } catch (err: any) {
      // Enhanced error handling
      let errorMessage = 'Failed to fetch quotes';
      
      if (err instanceof SyntaxError) {
        errorMessage = 'Server returned invalid JSON response';
      } else if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
        data: [],
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    createQuote,
    getQuotesByClient,
  };
};